import { useState, useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ArrowRight from '@/assets/icons/arrow_right_white.svg?react';
import ArrowLeft from '@/assets/icons/back_arrow_custom_color.svg?react';
import { useTourContext } from '@/contexts/TourContext';
import {
  NextStepModal,
  NextStepModalWrapper,
  TourBackdrop,
  TourHighlightGlobalStyle
} from '@/components/Tour/Tour.styled';
import { DynamicTourProps } from './Tour.types';
import { Button } from '@companio/companio_ui';

const MODAL_WIDTH = 443;

const arePositionsEqual = (pos1: any, pos2: any) => {
  if (!pos1 || !pos2) return false;
  return JSON.stringify(pos1) === JSON.stringify(pos2);
};

export function DynamicTour({ steps, onComplete, onSkip }: DynamicTourProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tourSteps, setTourSteps } = useTourContext();

  const [dynamicPos, setDynamicPos] = useState<any | null>(null);
  const lastPosRef = useRef<any>(null);

  const currentStepIndex = tourSteps - 1;
  const currentStepInfo = steps[currentStepIndex];
  const totalSteps = steps.length;

  const calculatePosition = () => {
    if (!currentStepInfo) return null;
    if (!currentStepInfo.isAnchorCard) return null;

    let targetEl: HTMLElement | null = null;
    let highlightEl: Element | null = null;

    if (currentStepInfo.elementId) {
      targetEl = document.getElementById(currentStepInfo.elementId);
      if (!targetEl) {
        return null;
      }

      // highlight logic
      if (currentStepInfo.targetSelector) {
        highlightEl = targetEl.closest(currentStepInfo.targetSelector);
      }
      if (!highlightEl) highlightEl = targetEl;
    } else if (currentStepInfo.targetSelector) {
      const el = document.querySelector(currentStepInfo.targetSelector);
      if (!el) return null;
      targetEl = el as HTMLElement;
      highlightEl = el;
    }

    if (!targetEl) return null;

    const targetRect = targetEl.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    if (targetRect.width === 0 && targetRect.height === 0) return null;

    let newTop = 'auto';
    let newBottom = 'auto';
    let newLeft = 'auto';
    let arrowDir = 'left';
    let arrowTop = 'auto';
    let arrowBottom = 'auto';
    let arrowLeft = 'auto';
    let arrowRight = 'auto';
    let calcMaxHeight = 500;

    const placement = currentStepInfo.placement || 'bottom';

    if (placement === 'left') {
      // position x
      const leftVal = targetRect.left - MODAL_WIDTH - 40;
      newLeft = `${Math.max(20, leftVal)}px`;

      // position y
      const topVal = targetRect.top - 20;
      newTop = `${topVal}px`;

      // arrow direction
      arrowDir = 'right';

      // arrow position
      arrowRight = '-25px';

      const btnCenterY = targetRect.top + targetRect.height / 2;
      const arrowY = btnCenterY - topVal - 16;
      arrowTop = `${arrowY}px`;

      calcMaxHeight = viewportHeight - topVal - 20;
    } else {
      // horizontal center of target element
      const elementCenterX = targetRect.left + targetRect.width / 2;

      // modal left position
      let leftPos = elementCenterX - MODAL_WIDTH / 2;

      // clamp horizontal
      if (leftPos < 20) leftPos = 20;
      if (leftPos + MODAL_WIDTH > viewportWidth - 60) {
        leftPos = viewportWidth - MODAL_WIDTH - 60;
      }
      newLeft = `${leftPos}px`;

      // arrow x position
      const calculatedArrowLeft = elementCenterX - leftPos - 16;
      arrowLeft = `${calculatedArrowLeft}px`;

      // clamp arrow
      const arrowNum = parseFloat(arrowLeft);
      if (!isNaN(arrowNum)) {
        if (arrowNum < 12) arrowLeft = '12px';
        if (arrowNum > MODAL_WIDTH - 44) arrowLeft = `${MODAL_WIDTH - 44}px`;
      }

      // vertical logic (top/bottom)
      const spaceBelow = viewportHeight - targetRect.bottom;
      const spaceAbove = targetRect.top;
      const COMFORTABLE_HEIGHT = 350;

      let placementSide = 'bottom';

      if (placement.includes('bottom')) {
        if (spaceBelow < COMFORTABLE_HEIGHT && spaceAbove > spaceBelow) {
          placementSide = 'top';
        }
      } else if (placement.includes('top')) {
        if (spaceAbove < COMFORTABLE_HEIGHT && spaceBelow > spaceAbove) {
          placementSide = 'bottom';
        } else {
          placementSide = 'top';
        }
      }

      if (placementSide === 'bottom') {
        newTop = `${targetRect.bottom + 24}px`;
        arrowDir = 'up';
        arrowTop = '-14px';
        calcMaxHeight = viewportHeight - (targetRect.bottom + 30) - 20;
      } else {
        newBottom = `${viewportHeight - targetRect.top + 24}px`;
        arrowDir = 'down';
        arrowBottom = '-14px';
        calcMaxHeight = targetRect.top - 40;
      }
    }

    if (calcMaxHeight < 100) calcMaxHeight = 100;

    return {
      top: newTop,
      bottom: newBottom,
      left: newLeft,
      arrowDirection: arrowDir,
      arrowTop,
      arrowBottom,
      arrowLeft,
      arrowRight,
      maxHeight: calcMaxHeight,
      _highlightElement: highlightEl
    };
  };

  useEffect(() => {
    setDynamicPos(null);
    lastPosRef.current = null;
    document
      .querySelectorAll('.tour-highlight-element')
      .forEach((el) => el.classList.remove('tour-highlight-element'));

    if (!currentStepInfo?.isAnchorCard) return;

    const updatePosition = () => {
      const newPos = calculatePosition();
      if (newPos) {
        const { _highlightElement, ...posData } = newPos;

        if (!arePositionsEqual(lastPosRef.current, posData)) {
          lastPosRef.current = posData;
          setDynamicPos(posData);
        }

        if (_highlightElement && !_highlightElement.classList.contains('tour-highlight-element')) {
          _highlightElement.classList.add('tour-highlight-element');
          _highlightElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest'
          });
        }
      }
    };

    updatePosition();

    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      for (const mutation of mutations) {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          shouldUpdate = true;
          break;
        }
      }
      if (shouldUpdate) updatePosition();
    });

    observer.observe(document.body, { childList: true, subtree: true, attributes: true });

    const handleResize = () => {
      lastPosRef.current = null;
      updatePosition();
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [tourSteps, currentStepInfo]);

  const handleNext = () => {
    if (currentStepIndex >= totalSteps - 1) onComplete();
    else {
      if (currentStepInfo.navigateToPath) navigate(currentStepInfo.navigateToPath);
      setTourSteps((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (tourSteps > 1) setTourSteps((prev) => prev - 1);
    if (currentStepInfo.prevPath) navigate(currentStepInfo.prevPath);
  };

  if (!currentStepInfo || tourSteps === 0) return null;
  if (currentStepInfo.isAnchorCard && !dynamicPos) return null;

  const finalTop = dynamicPos?.top ?? currentStepInfo?.staticPosition?.top;
  const finalBottom = dynamicPos?.bottom ?? currentStepInfo?.staticPosition?.bottom;
  const finalLeft = dynamicPos?.left ?? currentStepInfo?.staticPosition?.left;
  const finalArrowTop = dynamicPos?.arrowTop ?? currentStepInfo?.staticPosition?.arrowTop;
  // @ts-ignore
  const finalArrowBottom = dynamicPos?.arrowBottom ?? currentStepInfo?.staticPosition?.arrowBottom;
  const finalArrowLeft = dynamicPos?.arrowLeft;
  const finalArrowRight = dynamicPos?.arrowRight;

  const finalArrowDirection =
    dynamicPos?.arrowDirection ?? currentStepInfo?.staticPosition?.arrowDirection ?? 'left';
  const finalTransform = dynamicPos ? 'none' : currentStepInfo?.staticPosition?.transform;
  const showBackdrop = currentStepInfo?.isAnchorCard;

  return (
    <>
      <TourHighlightGlobalStyle />
      {showBackdrop && <TourBackdrop />}

      <NextStepModalWrapper
        $top={finalTop}
        $bottom={finalBottom}
        $left={finalLeft}
        $right={currentStepInfo.staticPosition?.right}
        $transform={finalTransform}
        $showArrow={dynamicPos ? true : currentStepInfo.staticPosition?.showArrow}
        $arrowTop={finalArrowTop}
        $arrowBottom={finalArrowBottom}
        $arrowLeft={finalArrowLeft}
        $arrowRight={finalArrowRight}
        $arrowDirection={finalArrowDirection}>
        <NextStepModal $maxHeight={dynamicPos?.maxHeight}>
          <div className="title-container">
            <div className="step-count-container">
              <p className="step-count">{`${tourSteps} of ${totalSteps}`}</p>
              <button className="skip" type="button" onClick={onSkip}>
                {t('skip')}
              </button>
            </div>
            <p className="step-title">{currentStepInfo.title}</p>
          </div>

          {currentStepInfo.image && <img src={currentStepInfo.image} alt="" />}

          <div className="step-description">
            <Trans
              i18nKey={currentStepInfo.description}
              components={{ strong: <strong />, p: <p />, li: <li /> }}
            />
          </div>

          <div className="buttons-row">
            {tourSteps > 1 && (
              <button className="prev" type="button" onClick={handlePrev}>
                <ArrowLeft /> {t('previous')}
              </button>
            )}

            {tourSteps < totalSteps ? (
              <button className="next" type="button" onClick={handleNext}>
                {t('next')} <ArrowRight />
              </button>
            ) : (
              <Button className="end-tour" type="button" onClick={onComplete} $variant="white">
                {t('end_tour')}
              </Button>
            )}
          </div>
        </NextStepModal>
      </NextStepModalWrapper>
    </>
  );
}
